var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getItemsType, uriToEntities, drilldown, navigate, resourceToUri, extractBreadcrumbs, isDrilldownAllowed, isItemsTypeSelectable } from '../helpers/uri';
import * as validationRules from '../helpers/validation-rules';
import { S3_RESOURCE_SELECTOR_ITEM_TYPES, versionToOption, getSortableColumns, DEFAULT_VISIBLE_COLUMNS, DEFAULT_IS_ITEM_DISABLED, sortColumnDefinitions } from '../helpers/s3-config';
import { defaultColumnDefinitions } from '../helpers/column-definitions';
import { cellTemplates } from '../helpers/cell-templates';
import { focusUriInput, focusBrowseButton, focusFilteringInput } from './focus-controller';
export var getValidationErrorString = function (validationErrorStrings, uri) {
    if (uri === '') {
        return null;
    }
    var _a = uriToEntities(uri)[0], bucketName = _a === void 0 ? '' : _a;
    var failedRuleName = Object.keys(validationErrorStrings).filter(function (ruleName) {
        return !validationRules[ruleName]({
            uri: uri,
            bucketName: bucketName
        });
    })[0];
    return failedRuleName ? validationErrorStrings[failedRuleName] : null;
};
export var getChangeEventDetail = function (component, newResource) {
    var resource = __assign(__assign({}, component.resource), newResource);
    var updateDetail = {
        resource: resource,
        errorText: getValidationErrorString(component.__state.validationErrorStrings, resource.uri)
    };
    return updateDetail;
};
export var fireUpdate = function (component, resource) {
    var updateDetail = getChangeEventDetail(component, resource);
    updateDetail.resource.versionId = null;
    component.__fireEvent('update', updateDetail);
};
export var fireChange = function (component, resource) {
    var updateDetail = getChangeEventDetail(component, resource);
    component.__fireEvent('change', updateDetail);
};
export var fireFetch = function (component, resource) {
    var updateDetail = getChangeEventDetail(component, resource);
    var itemsType = getItemsType(resource);
    component.__fireEvent('fetch', __assign(__assign({}, updateDetail), { itemsType: itemsType }));
};
export var initInternalState = function (component) {
    return __assign(__assign({}, component.__state), { validationErrorStrings: validationRules.validationErrorStrings(component.i18nStrings) });
};
export var makeInitialFetchCall = function (component) {
    component.resource && component.resource.uri !== '' && fireFetch(component, component.resource);
};
export var modalSubmit = function (component) {
    var currentItemsType = component.currentItemsType, resource = component.resource, __state = component.__state;
    var itemsType = currentItemsType.toLowerCase();
    var selectedItem = __state.selectedItems[0];
    if (itemsType === S3_RESOURCE_SELECTOR_ITEM_TYPES.VERSIONS) {
        fireChange(component, { versionId: selectedItem.ID });
    }
    else {
        var nextResource = drilldown(resource, selectedItem.Name || selectedItem.Key);
        fireChange(component, nextResource);
        var isFile = itemsType === S3_RESOURCE_SELECTOR_ITEM_TYPES.OBJECTS &&
            selectedItem.IsFolder === false;
        if (isFile && isDrilldownAllowed(component.selectableItemsTypes, itemsType)) {
            fireFetch(component, nextResource);
        }
    }
    component.__fireEvent('modalSubmit', { selectedItem: selectedItem });
    focusUriInput(component);
    return __assign(__assign({}, __state), { isModalOpen: false });
};
export var modalDismiss = function (component, detail) {
    component.__fireEvent('modalDismiss', detail);
    focusBrowseButton(component);
    return __assign(__assign({}, component.__state), { isModalOpen: false });
};
export var uriChange = function (component, detail) {
    var uri = detail.value;
    if (uri !== '') {
        fireFetch(component, { uri: uri });
        fireChange(component, { uri: uri });
    }
};
export var browse = function (component) {
    component.__fireEvent('browse');
    fireFetch(component, { uri: '' });
    return __assign(__assign({}, component.__state), { isModalOpen: true, selectedItems: [], filteringText: '', sortingColumn: '', sortingDescending: false });
};
export var refresh = function (component) {
    component.__fireEvent('refresh');
    return __assign(__assign({}, component.__state), { selectedItems: [] });
};
export var selectionChange = function (component, detail) {
    return __assign(__assign({}, component.__state), { selectedItems: detail.selectedItems });
};
export var filteringChange = function (component, detail) {
    component.__fireEvent('filteringChange', detail);
    return __assign(__assign({}, component.__state), { filteringText: detail.filteringText });
};
export var sortingChange = function (component, detail) {
    component.__fireEvent('sortingChange', detail);
    return __assign(__assign({}, component.__state), { sortingColumn: detail.sortingColumn, sortingDescending: detail.sortingDescending });
};
export var versionChange = function (component, detail) {
    fireChange(component, { versionId: detail.selectedOption.id });
};
export var handleNavigate = function (component, detail) {
    fireFetch(component, navigate(component.resource, parseInt(detail.href)));
    focusFilteringInput(component);
    return __assign(__assign({}, component.__state), { selectedItems: [], filteringText: '' });
};
export var handleDrilldown = function (component, item) {
    fireFetch(component, drilldown(component.resource, item.Name || item.Key));
    focusFilteringInput(component);
    return __assign(__assign({}, component.__state), { selectedItems: [], filteringText: '' });
};
export var getInContextProps = function (component) {
    var _a;
    var uri = resourceToUri(component.resource);
    var showVersionSelector = isItemsTypeSelectable(component.selectableItemsTypes, S3_RESOURCE_SELECTOR_ITEM_TYPES.VERSIONS);
    var options = component.versions.map(versionToOption);
    var selectedVersionId = (_a = component.resource) === null || _a === void 0 ? void 0 : _a.versionId;
    return {
        uri: uri,
        showVersionSelector: showVersionSelector,
        options: options,
        selectedVersionId: selectedVersionId
    };
};
export var getModalProps = function (component) {
    return {
        breadcrumbs: extractBreadcrumbs(component.i18nStrings.modalBreadcrumbRootItem, component.resource),
        isSubmitEnabled: component.__state.selectedItems.length > 0
    };
};
export var getIsDrilldownAllowedForItem = function (component) {
    var itemsType = component.currentItemsType.toLowerCase();
    var drilldownAllowed = isDrilldownAllowed(component.selectableItemsTypes, itemsType);
    if (drilldownAllowed) {
        return function () { return true; };
    }
    if (itemsType === S3_RESOURCE_SELECTOR_ITEM_TYPES.OBJECTS) {
        return function (item) { return item.IsFolder === true; };
    }
    return function () { return false; };
};
export var getCellTemplate = function (component) {
    var _a, _b;
    var isDrilldownAllowedForItem = getIsDrilldownAllowedForItem(component);
    return (_b = (_a = cellTemplates)[component.currentItemsType]) === null || _b === void 0 ? void 0 : _b.call(_a, component.__drilldown, isDrilldownAllowedForItem);
};
export var getSelectionProps = function (component) {
    var _a;
    var itemsType = component.currentItemsType.toLowerCase();
    var cellTemplate = getCellTemplate(component);
    var customVisibleColumns = component[itemsType + "VisibleColumns"];
    var visibleColumns = ((_a = customVisibleColumns) === null || _a === void 0 ? void 0 : _a.length) > 0 ? customVisibleColumns : DEFAULT_VISIBLE_COLUMNS[itemsType];
    var columnDefinitions = defaultColumnDefinitions[itemsType](component.i18nStrings, cellTemplate);
    var sortedColumnDefinitions = sortColumnDefinitions(columnDefinitions, visibleColumns);
    var sortableColumns = getSortableColumns(sortedColumnDefinitions);
    var items = component[itemsType.toLowerCase()];
    var itemsSelectable = isItemsTypeSelectable(component.selectableItemsTypes, itemsType);
    var customIsItemDisabled = component[itemsType + "IsItemDisabled"];
    var isItemDisabled = customIsItemDisabled
        ? customIsItemDisabled
        : itemsSelectable
            ? DEFAULT_IS_ITEM_DISABLED[itemsType]
            : function () { return true; };
    return {
        itemsType: itemsType,
        drilldown: drilldown,
        columnDefinitions: sortedColumnDefinitions,
        sortableColumns: sortableColumns,
        isItemDisabled: isItemDisabled,
        items: items,
        visibleColumns: visibleColumns,
        filteringLabel: component.i18nStrings.labelFiltering(itemsType)
    };
};
//# sourceMappingURL=s3-resource-selector-controller.js.map